import { useEffect, useState } from 'react';
import { config } from './config';
import { getUserIdOrAnonymousId, retryWithDelay } from './utils';

interface FeatureFlags {
  vo_demo_downloadLimit: number;
  vo_demo_isUpsellEnabled: boolean;
  vo_demo_generationLimit: number;
}

interface FeatureFlagStatus<K extends keyof FeatureFlags> {
  error?: Error;
  variant: FeatureFlags[K];
}

type FeatureFlagHookStatus<K extends keyof FeatureFlags> =
  FeatureFlagStatus<K> & {
    isLoading: boolean;
  };

let featureFlagsPromise:
  | Promise<Record<string, FeatureFlags[keyof FeatureFlags]>>
  | undefined;

const KEY = 'localPreferences';

interface localPreferences {
  vo_demo_feature_flags?: string;
}

async function getFeatures(): Promise<any> {
  const analyticsId = await retryWithDelay(getUserIdOrAnonymousId, 100, 25, 0);
  const resp = await fetch(`${config.demoBackendUrl}/features/${analyticsId}`, {
    method: 'GET',
  });
  if (!resp.ok) {
    throw new Error('error occured while fetching feature flag');
  }
  return await resp.json();
}

export function getLocalPreference(preference?: keyof localPreferences) {
  const currentPreferencesString = localStorage.getItem(KEY) ?? '{}';
  const currentPreferences = JSON.parse(
    currentPreferencesString,
  ) as localPreferences;

  return preference ? currentPreferences?.[preference] : currentPreferences;
}
function getFeatureFlagsFromLocalStorage() {
  return getLocalPreference('vo_demo_feature_flags') as Record<
    string,
    FeatureFlags[keyof FeatureFlags]
  >;
}

function setFeatureFlagsInLocalStorage(featureFlags: object) {
  updateLocalPreferences({
    vo_demo_feature_flags: JSON.stringify(featureFlags),
  });
}

export function loadFeatureFlags() {
  if (!featureFlagsPromise) {
    featureFlagsPromise = (async () => {
      const featureFlags: Record<string, FeatureFlags[keyof FeatureFlags]> =
        await getFeatures();

      if (!featureFlags || Object.keys(featureFlags).length === 0) {
        const cachedFeatureFlags = getFeatureFlagsFromLocalStorage();
        if (cachedFeatureFlags) {
          return cachedFeatureFlags;
        }
        throw new Error('Unable to load data from configcat');
      } else {
        setFeatureFlagsInLocalStorage(featureFlags);
        return featureFlags;
      }
    })();
  }
  return featureFlagsPromise;
}
export function updateLocalPreferences(preferences: localPreferences) {
  const currentPreferences = getLocalPreference() as localPreferences;
  const updatedPreferences = { ...currentPreferences, ...preferences };

  localStorage.setItem(KEY, JSON.stringify(updatedPreferences));
}

export function useFeatureFlag<K extends keyof FeatureFlags>(
  feature: K,
  defaultValue: FeatureFlags[K],
): FeatureFlagHookStatus<K> {
  const [status, setStatus] = useState<FeatureFlagHookStatus<K>>({
    isLoading: true,
    variant: defaultValue,
  });

  useEffect(() => {
    if (!featureFlagsPromise) return;

    loadFeatureFlags()
      ?.then((features) => {
        const variant = features[feature] as FeatureFlags[K];
        setStatus({ isLoading: false, variant: variant ?? defaultValue });
      })
      .catch((error) => {
        setStatus({
          isLoading: false,
          error: error as Error,
          variant: defaultValue,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature, featureFlagsPromise]);
  return status;
}
